import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Badge } from '@/components/ui/badge';
import { Star, Quote } from 'lucide-react';

const testimonials = [
  {
    name: 'Priya Sharma',
    role: 'Founder, StyleKart',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&crop=face',
    quote: 'Agentick transformed how we handle customer queries. Our response time went from hours to seconds. The setup was incredibly simple.',
    rating: 5,
  },
  {
    name: 'Rajesh Patel',
    role: 'CEO, GreenGrocer India',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=face',
    quote: 'We were spending ₹30,000/month on customer support. Agentick cut that by 70% while actually improving customer satisfaction.',
    rating: 5,
  },
  {
    name: 'Anita Desai',
    role: 'Owner, Spice Route Café',
    image: 'https://images.pexels.com/photos/10375901/pexels-photo-10375901.jpeg?w=100&h=100&fit=crop&crop=face',
    quote: 'The best part? No Business API hassles. We were live in 10 minutes. Our customers love getting instant replies on WhatsApp.',
    rating: 5,
  },
];

export const Testimonials = () => {
  const [ref, isVisible] = useScrollAnimation(0.1);

  return (
    <section id="testimonials" ref={ref} className="py-24 lg:py-32 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-primary/[0.03] blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="premium" className="mb-4">
              Social Proof
            </Badge>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight"
          >
            Loved by Indian SMBs
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base lg:text-lg text-muted-foreground font-body leading-relaxed"
          >
            Join businesses across India who are already using Agentick to grow.
          </motion.p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + i * 0.15 }}
              className="group"
            >
              <div className="glass-card rounded-2xl p-7 h-full flex flex-col hover:border-primary/15 transition-all duration-500">
                {/* Quote icon */}
                <Quote size={24} className="text-primary/30 mb-4" />

                {/* Rating */}
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.rating }).map((_, si) => (
                    <Star key={si} size={14} className="fill-accent text-accent" />
                  ))}
                </div>

                {/* Quote text */}
                <p className="text-sm text-secondary-foreground leading-relaxed font-body mb-6 flex-1">
                  "{t.quote}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 mt-auto">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground font-heading">{t.name}</p>
                    <p className="text-xs text-muted-foreground font-body">{t.role}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="flex flex-wrap justify-center gap-4 mt-12"
        >
          {['Made for Indian SMBs', 'GDPR Compliant', 'Enterprise-grade Security', '24/7 Support'].map((badge) => (
            <Badge key={badge} variant="glass" className="text-xs px-4 py-1.5">
              {badge}
            </Badge>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
